import { errorRetryability } from './generated/contract.ts';
import type { ErrorCode, ErrorEnvelope } from './generated/models.ts';

/**
 * What a call can fail as. Two classes and no more: the API refused, or nothing
 * usable came back from it.
 */

/**
 * Whether the API's own error registry says a code may be replayed.
 *
 * Retryability is contract, not advice. Every authentication and authorisation
 * code is non-retryable — those refusals are caused by the client and would
 * fail identically on replay — and the answer is generated from the registry
 * table in the document rather than restated here, so it cannot drift.
 *
 * Anything the registry does not answer for is **not** retryable: an absent
 * code (a proxy answered, not the API) and a code from an API deployed ahead of
 * this client are both cases where nothing can be reasoned about, and retrying
 * the unreasoned is how a terminal loops on a refusal at the head of its queue.
 */
export function isRetryableErrorCode(code: string | undefined): boolean {
  if (code === undefined) return false;

  // The registry's keys are exactly the document's ErrorCode union, but the
  // code arriving on the wire is whatever the deployed API sent — which may be
  // newer than this client. Widened so the lookup can miss.
  const registry: Record<string, boolean | undefined> = errorRetryability;

  // Only the registry's own rows may answer. The code is attacker-choosable —
  // a captive portal or a proxy answering in the API's place picks it — and a
  // plain lookup of `constructor` or `toString` resolves up the prototype chain
  // instead of missing, so `??` never fires and a refusal the client caused
  // comes back truthy. That is the loop this flag exists to prevent.
  return Object.hasOwn(errorRetryability, code) ? (registry[code] ?? false) : false;
}

/**
 * A refusal from the API — anything that answered with a non-success status.
 *
 * Screens branch on `code`, never on `message`: the API's prose is for humans
 * and logs and may change freely (Constitution III). `code` is absent when the
 * failure did not come from the API itself — a proxy, a gateway — and nothing
 * is invented to fill it, because a mapped message for something the API never
 * said is worse than a generic one.
 *
 * `retryable` is the registry's answer, not a guess from the status: a 409 is
 * `SYNC_REQUEST_IN_PROGRESS` (retry shortly) or `SYNC_IDEMPOTENCY_KEY_REUSED`
 * (never), and the status alone cannot tell them apart. Retrying is the
 * caller's decision — the transport does not retry — but the flag it decides
 * from comes from here.
 */
export class ApiError extends Error {
  readonly status: number;
  readonly code: ErrorCode | undefined;
  readonly requestId: string | undefined;
  readonly details: Record<string, unknown> | undefined;
  readonly retryable: boolean;
  readonly envelope: ErrorEnvelope | undefined;

  constructor(status: number, envelope: ErrorEnvelope | undefined, options?: { cause?: unknown }) {
    super(envelope?.message ?? `The API answered ${status}.`, { cause: options?.cause });
    this.name = 'ApiError';
    this.status = status;
    this.code = envelope?.code;
    this.requestId = envelope?.requestId;
    this.details = envelope?.details;
    this.retryable = isRetryableErrorCode(envelope?.code);
    this.envelope = envelope;
  }
}

/**
 * No usable answer came back from the API: no connection, DNS failure, a
 * terminal that has lost the network, a redirect the client refused to follow,
 * or a success status carrying a body that is not the JSON it claims to be.
 *
 * Deliberately a separate class from `ApiError`. On a till, offline is an
 * expected operating mode rather than an error, and a screen that cannot tell
 * "the network is down" from "the server refused you" will word one of them
 * wrongly.
 *
 * The unparseable-body cases belong here rather than in a third class because
 * they have the same cause and the same remedy: something between the till and
 * the API — a captive portal, a proxy, a cut connection — is answering in the
 * API's place. That is the network being wrong, which is what this already
 * means, and a third class would make every screen handle a fifth state.
 */
export class ApiNetworkError extends Error {
  constructor(message: string, options?: { cause?: unknown }) {
    super(message, { cause: options?.cause });
    this.name = 'ApiNetworkError';
  }
}
